import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_ORIGIN } from '@/lib/site';

const BASE_TITLE = 'My Study Fund | Student Fundraising for Education';
const SHORT_BRAND = 'My Study Fund';
const ORIGIN = SITE_ORIGIN;

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
    return;
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

/**
 * Sets document title and meta description for the current page.
 * Also keeps og:title / og:description / twitter:* in sync so shared links
 * and prerendered pages carry page-specific metadata.
 * @param pageTitle - For homepage pass undefined or empty; for other pages pass e.g. "About", "Pricing"
 * @param description - Meta description content (optional; omit to leave description unchanged)
 * @param articleMeta - Pass for article-type pages (e.g. blog posts) to set og:type=article plus
 *   article:published_time/modified_time, which social platforms use for richer link previews.
 *   Omit for ordinary pages — og:type is reset to "website" whenever this is undefined.
 */
export function usePageMeta(
  pageTitle: string | undefined,
  description?: string,
  articleMeta?: { publishedTime: string; modifiedTime?: string },
) {
  const publishedTime = articleMeta?.publishedTime;
  const modifiedTime = articleMeta?.modifiedTime;

  useEffect(() => {
    // Google truncates (and often outright rewrites) a <title> past ~60 chars.
    // The page's own TITLE already carries the distinctive, click-worthy part
    // (and on comparison pages already says "My Study Fund") -- appending the
    // full homepage tagline pushed every resource-page title past 150 chars,
    // guaranteeing the differentiator got cut from the SERP snippet.
    const fullTitle = pageTitle
      ? pageTitle.toLowerCase().includes('my study fund')
        ? pageTitle
        : `${pageTitle} | ${SHORT_BRAND}`
      : BASE_TITLE;
    document.title = fullTitle;
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: fullTitle });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: fullTitle });

    if (description !== undefined) {
      upsertMeta('meta[name="description"]', { name: 'description', content: description });
      upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
      upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    }

    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: publishedTime ? 'article' : 'website',
    });
    if (publishedTime) {
      upsertMeta('meta[property="article:published_time"]', {
        property: 'article:published_time',
        content: publishedTime,
      });
      upsertMeta('meta[property="article:modified_time"]', {
        property: 'article:modified_time',
        content: modifiedTime ?? publishedTime,
      });
    } else {
      document.head.querySelector('meta[property="article:published_time"]')?.remove();
      document.head.querySelector('meta[property="article:modified_time"]')?.remove();
    }
  }, [pageTitle, description, publishedTime, modifiedTime]);
}

/**
 * Marks the current page noindex,nofollow. Mount in pages that must never be
 * indexed (404s, internal-only routes) — without it they inherit whatever
 * robots meta the SPA shell/prerender already wrote for a real route.
 */
export function useRobotsNoIndex() {
  useEffect(() => {
    upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex,nofollow' });
    return () => {
      upsertMeta('meta[name="robots"]', { name: 'robots', content: 'index,follow' });
    };
  }, []);
}

/**
 * Keeps <link rel="canonical"> and og:url pointing at the current route.
 * Without this, every prerendered page ships the homepage canonical and
 * search engines treat all pages as duplicates of "/".
 * Mount once at the app root.
 */
export function useCanonicalSync() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Normalize: strip trailing slash except for root
    const path = pathname !== '/' && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const url = `${ORIGIN}${path}`;

    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', url);
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
  }, [pathname]);
}
